import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './StripePages.css';
import { ensureFirebaseApp } from '../utils/mobileWebAuth';

// Try to open the mobile app via its custom URL scheme WITHOUT navigating the
// current page away. A plain `window.location.href = 'fodyfee://...'` inside a
// setTimeout (i.e. not a direct user click) is blocked or only partially
// handled by several mobile browsers, which can leave the tab on a blank page
// with no visible way back if the app doesn't actually open. Using a hidden
// iframe lets the OS intercept the scheme if it can, while leaving this page
// (and its "Return to Mobile App" button, which uses a real click instead)
// intact either way.
function tryOpenAppSilently(url) {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 2000);
  } catch (e) {
    console.error('[Return] Silent app-open attempt failed:', e);
  }
}

function ReturnPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);
  const source = searchParams.get('source') === 'mobile' ? 'mobile' : 'web';
  const appReturnUrl = searchParams.get('app_return') || 'fodyfee://stripe/return';
  const returnToken = (searchParams.get('return_token') || '').trim();
  const mobileQuery = `source=mobile&app_return=${encodeURIComponent(appReturnUrl)}`;
  const plansPath = source === 'mobile' ? `/admin/plans?${mobileQuery}` : '/admin/plans';

  // Auto-attempt to return to the app once we have a definitive result, but
  // only for 'success' — for 'pending'/'partial' we don't know enough to whisk
  // the user away, so we just let them read the message and tap the button
  // themselves (a real click, which is far more reliable for custom-scheme
  // navigation across mobile browsers than a timer-based redirect).
  useEffect(() => {
    if (status !== 'success') return undefined;

    if (source === 'mobile') {
      const timer = setTimeout(() => tryOpenAppSilently(appReturnUrl), 600);
      return () => clearTimeout(timer);
    }

    if (user) {
      const timer = setTimeout(() => {
        navigate(plansPath);
      }, 1500);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [status, user, source, appReturnUrl, navigate, plansPath]);

  useEffect(() => {
    ensureFirebaseApp();
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        verifyStripeStatus(currentUser.uid);
      } else if (source === 'mobile' && returnToken) {
        // No Firebase session in this external browser (expected for the
        // mobile flow) — prove identity with the signed return token minted
        // by createStripeAccountLink instead of trusting a raw userId.
        verifyStripeStatus(null, returnToken);
      } else {
        // No verified session or return token available — fall back to the
        // redirect query params Stripe/our own onboarding flow may have
        // appended, instead of trusting an unauthenticated userId.
        const accountStatus = searchParams.get('account_status');
        const chargesEnabled = searchParams.get('charges_enabled');

        if (accountStatus === 'enabled' && chargesEnabled === 'true') {
          setStatus('success');
        } else if (accountStatus === 'enabled') {
          setStatus('partial');
        } else {
          setStatus('pending');
        }
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, source, returnToken]);

  const verifyStripeStatus = async (uid, token) => {
    try {
      const functions = getFunctions();
      const getStripeAccountStatus = httpsCallable(functions, 'getStripeAccountStatus');
      const result = await getStripeAccountStatus(token ? { returnToken: token } : { userId: uid });
      const data = result.data;

      if (data.success && data.connected) {
        const fullyVerified = data.chargesEnabled && data.payoutsEnabled && data.detailsSubmitted;
        // Firestore is now synced server-side by getStripeAccountStatus itself
        // (needed for the mobile/return-token flow, which has no Firebase Auth
        // session in this browser and so couldn't write to Firestore directly
        // even if we wanted to — that write would be rejected by security
        // rules). No client-side setDoc needed here anymore.
        setStatus(fullyVerified ? 'success' : 'partial');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error verifying Stripe status:', error);
      // Fallback to URL params
      const accountStatus = searchParams.get('account_status');
      const chargesEnabled = searchParams.get('charges_enabled');
      if (accountStatus === 'enabled' && chargesEnabled === 'true') {
        setStatus('success');
      } else if (accountStatus === 'enabled') {
        setStatus('partial');
      } else {
        setStatus('pending');
      }
    }
  };

  const handleReturnToApp = () => {
    if (source === 'mobile') {
      window.location.href = appReturnUrl;
      return;
    }

    if (user) {
      navigate(plansPath);
    } else {
      navigate('/admin/login');
    }
  };

  const handleOpenMobileApp = () => {
    // Deep link to mobile app
    window.location.href = appReturnUrl;
  };

  const handleContinueOnWeb = () => {
    if (status === 'success') {
      navigate(plansPath);
      return;
    }
    navigate(source === 'mobile' ? `/admin/onboarding?${mobileQuery}` : '/admin/onboarding');
  };

  const handleSignOut = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const floatingIngredients = (
    <div className="ff-hero-ingredients">
      <div className="ff-ingredient tomato"></div>
      <div className="ff-ingredient pepper"></div>
      <div className="ff-ingredient mushroom"></div>
      <div className="ff-ingredient leaf"></div>
      <div className="ff-ingredient onion"></div>
      <div className="ff-ingredient carrot"></div>
      <div className="ff-ingredient garlic"></div>
      <div className="ff-ingredient broccoli"></div>
      <div className="ff-ingredient banana"></div>
      <div className="ff-ingredient bread"></div>
      <div className="ff-ingredient egg"></div>
      <div className="ff-ingredient sausage"></div>
      <div className="ff-ingredient cheese"></div>
      <div className="ff-ingredient lemon"></div>
      <div className="ff-ingredient strawberry"></div>
    </div>
  );

  if (status === 'success') {
    return (
      <div className="stripe-page success" style={{ position: 'relative', overflow: 'hidden' }}>
        {floatingIngredients}
        <div className="stripe-card">
          <div className="success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12l2 2 4-4" />
            </svg>
          </div>
          <h1>Account Connected!</h1>
          <p>Your Stripe account has been successfully set up. You're now ready to receive payments.</p>
          <div className="button-group">
            <button onClick={handleReturnToApp} className="primary-button">
              {source === 'mobile' ? 'Return to Mobile App' : 'Continue to Plans'}
            </button>
            {source === 'mobile' && (
              <button onClick={() => navigate(plansPath)} className="secondary-button">
                Continue on Web
              </button>
            )}
          </div>
          {source === 'mobile' && (
            <div style={{ marginTop: '16px' }}>
              <button
                onClick={handleOpenMobileApp}
                className="secondary-button"
                style={{ fontSize: '14px', padding: '10px 20px' }}
              >
                Open in Mobile App
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === 'partial') {
    return (
      <div className="stripe-page warning" style={{ position: 'relative', overflow: 'hidden' }}>
        {floatingIngredients}
        <div className="stripe-card">
          <div className="warning-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          </div>
          <h1>Almost There!</h1>
          <p>Your account is set up, but some verification steps are still pending. Please complete all required information.</p>
          <div className="button-group">
            {source === 'mobile' ? (
              <>
                <button onClick={handleReturnToApp} className="primary-button">
                  Return to Mobile App
                </button>
                <button onClick={handleContinueOnWeb} className="secondary-button">
                  Complete on Web
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/admin/onboarding')}
                className="primary-button"
              >
                Complete Onboarding
              </button>
            )}
          </div>
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              onClick={handleSignOut}
              style={{ fontSize: '14px', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-page pending" style={{ position: 'relative', overflow: 'hidden' }}>
      {floatingIngredients}
      <div className="stripe-card">
        <div className="pending-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        </div>
        <h1>Setup In Progress</h1>
        <p>
          {source === 'mobile'
            ? 'Tap the button below to return to the app and continue setup there.'
            : 'Please complete the Stripe onboarding process to start receiving payments.'}
        </p>
        <div className="button-group">
          {source === 'mobile' ? (
            <>
              <button onClick={handleReturnToApp} className="primary-button">
                Return to Mobile App
              </button>
              <button onClick={handleContinueOnWeb} className="secondary-button">
                Continue Setup on Web
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/admin/onboarding')}
              className="primary-button"
            >
              Continue Setup
            </button>
          )}
        </div>
        {source !== 'mobile' && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              onClick={handleSignOut}
              style={{ fontSize: '14px', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReturnPage;
